/**
 * Forwards parent slots (including named slots) into a bootstrap-vue child.
 * Mutates vnode.data.slot for non-default slots — standard Vue 2 wrapper pattern.
 */
export function forwardSlots(slots) {
  const result = [];
  Object.keys(slots || {}).forEach((name) => {
    const vnodes = slots[name];
    if (!vnodes) return;
    vnodes.forEach((vnode) => {
      if (name !== "default") {
        vnode.data = vnode.data || {};
        if (vnode.data.slot == null) {
          vnode.data.slot = name;
        }
      }
      result.push(vnode);
    });
  });
  return result;
}

/**
 * Default bridge for one bootstrap-vue widget. Each EgB* lives in `proxies/eg_b_*.js`;
 * replace that file with a .vue or custom implementation when dropping bootstrap-vue.
 *
 * The proxy uses Proxy to forward unknown property/method access to the inner
 * bootstrap-vue component instance, so that parent `$refs` work transparently
 * (e.g. `this.$refs.selectableTable.selectRow(i)`).
 */
export function createEgBootstrapProxy(name, Component) {
  return {
    name,
    inheritAttrs: false,
    render(h) {
      return h(
        Component,
        {
          ref: "inner",
          attrs: { ...this.$attrs },
          on: this.$listeners,
          scopedSlots: this.$scopedSlots,
        },
        forwardSlots(this.$slots)
      );
    },
    mounted() {
      this._proxyHandler = proxyMethodsFrom(this, () => this.$refs.inner);
    },
    beforeDestroy() {
      if (this._proxyHandler) {
        this._proxyHandler.revoke();
        this._proxyHandler = null;
      }
    },
  };
}

/**
 * Copies methods from the inner component instance onto the proxy so that
 * parent `$refs.foo.someMethod()` works as expected.
 */
function proxyMethodsFrom(proxy, getInner) {
  const inner = getInner();
  if (!inner || !inner.$options) return null;

  const copied = [];
  const methodKeys = Object.keys(inner.$options.methods || {});

  for (const key of methodKeys) {
    if (key in proxy) continue;
    try {
      proxy[key] = (...args) => {
        const target = getInner();
        return target && typeof target[key] === "function"
          ? target[key](...args)
          : undefined;
      };
      copied.push(key);
    } catch {
      // skip read-only properties
    }
  }

  return {
    revoke() {
      for (const key of copied) {
        try {
          delete proxy[key];
        } catch {
          // skip
        }
      }
    },
  };
}
